/**
 * Admin Copy Editor (Iter 9.28 rebuild) - Airtable-style grid.
 * Bulk-edit every UI string from admin_copy collection. Mirrors the
 * Category Master grid pattern: inline rows, per-row save, dirty markers,
 * search, and surface filter chips. Adds an AI rewrite popover and
 * a "+ Add row" inline creator.
 *
 * Data source: GET /api/admin-copy/full (admin only) - returns full rows
 *   {key, value, default_value, surface, group, description, locale, updated_at}
 * Writes: POST /api/admin-copy upsert / DELETE /api/admin-copy/{key}
 */
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft,
  Loader2,
  Save,
  RefreshCw,
  Search,
  Plus,
  Trash2,
  RotateCcw,
  Type,
  ChevronDown,
  ChevronRight
} from "lucide-react";
import { toast } from "sonner";
import AdminFieldInfo from "@/components/admin/AdminFieldInfo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Surface filter chips - same labels as the legacy editor for continuity
const SURFACE_OPTIONS = [
  { id: "all", label: "All" },
  { id: "public", label: "Public / Pre-Login" },
  { id: "onboarding", label: "Onboarding" },
  { id: "me_dashboard", label: "/me Dashboard" },
  { id: "preference_card", label: "Preference Card" },
  { id: "rsvp", label: "RSVP / Client Flow" },
  { id: "host_dashboard", label: "Host Dashboard" },
  { id: "admin", label: "Admin Surfaces" },
  { id: "system", label: "System / Errors" },
  { id: "email", label: "Email Templates" },
];

const COLUMNS = [
  { key: "key", label: "Key", width: "w-64", readonly: true },
  { key: "value", label: "Value (current)", width: "w-[28rem]", type: "text" },
  { key: "default_value", label: "Default", width: "w-[20rem]", muted: true },
  { key: "screen", label: "Screen", width: "w-32" },
  { key: "modal", label: "Modal", width: "w-36" },
  { key: "category", label: "Category", width: "w-28" },
  { key: "description", label: "Description", width: "w-56" },
];

// Iter 9.29: collapsible group ordering - screen → modal → category.
// User-friendly labels for the group headers.
const SCREEN_LABELS = {
  me_dashboard: "/me Dashboard",
  me_occasions: "/me Occasions",
  host_dashboard: "Host Dashboard",
  instructor_dashboard: "Instructor Dashboard",
  rsvp: "RSVP / Client Flow",
  confirmation_page: "Confirmation Page (/c/<id>)",
  sales_page: "Sales / Marketing Pages",
  landing: "Landing Page",
  admin: "Admin Surfaces",
  system: "System / Errors / Toasts",
  email: "Email Templates",
  category_master: "Category Master Strings",
  onboarding: "Onboarding / Signup",
};

const MODAL_LABELS = {
  none: "(no modal)",
  early_access_modal: "Early Access Modal",
  plan_experience_modal: "Plan a Service Modal",
  plan_occasion_modal: "Plan an Occasion Modal",
  share_modal: "Share Modal",
  intake_modal: "Intake / RSVP Modal",
  invite_modal: "Invite Modal",
  upgrade_modal: "Upgrade Modal",
  provider_waitlist_card: "Provider Waitlist Card",
  auth_modal: "Auth / Magic Link",
};

const headers = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("session_token")}`,
});

export default function AdminCopyEditorPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [drafts, setDrafts] = useState({}); // {key: {field: value}}
  const [search, setSearch] = useState("");
  const [surface, setSurface] = useState("all");
  const [adding, setAdding] = useState(false);
  const [newRow, setNewRow] = useState({ key: "", value: "", surface: "public", group: "", description: "" });
  // AI rewrite removed in iter 9.33
  // Iter 9.29: 3-tier collapsible group state. Each tier collapses independently.
  const [collapsedScreens, setCollapsedScreens] = useState(new Set());
  const [collapsedModals, setCollapsedModals] = useState(new Set());
  const [categoryFilter, setCategoryFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      // Try the new full-row endpoint first; fall back to the legacy registry endpoint
      let allRows = [];
      const fullRes = await fetch(`${API}/admin-copy/full`, { headers: headers() });
      if (fullRes.ok) {
        const d = await fullRes.json();
        allRows = d.rows || [];
      }
      if (allRows.length === 0) {
        // Fallback to the legacy copy_registry shape
        const legacy = await fetch(`${API}/admin/copy-registry`, { headers: headers() });
        if (legacy.ok) {
          const d = await legacy.json();
          allRows = (d.copies || []).map(c => ({
            key: c.surface_id ? `${c.surface_id}.${c.field || "value"}` : (c.copy_key || c.copy_id),
            value: c.value ?? c.current_text ?? c.default_text ?? "",
            default_value: c.default_value ?? c.default_text ?? "",
            surface: c.surface_type || "public",
            group: c.group || "",
            description: c.description || "",
            locale: c.locale || "en",
            _legacy_id: c.copy_id,
          }));
        }
      }
      // Sort alphabetically by key for deterministic Airtable feel
      allRows.sort((a, b) => (a.key || "").localeCompare(b.key || ""));
      setRows(allRows);
    } catch (e) {
      toast.error("Could not load copy entries: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const lc = search.toLowerCase();
    return rows.filter(r => {
      if (surface !== "all" && r.surface !== surface) return false;
      if (categoryFilter !== "all" && (r.category || "all") !== categoryFilter) return false;
      if (!lc) return true;
      const hay = `${r.key} ${r.value || ""} ${r.default_value || ""} ${r.description || ""} ${r.group || ""} ${r.screen || ""} ${r.modal || ""} ${r.category || ""}`.toLowerCase();
      return hay.includes(lc);
    });
  }, [rows, search, surface, categoryFilter]);

  // Iter 9.29: build the screen → modal → rows tree from filtered list.
  const grouped = useMemo(() => {
    const tree = {};
    filtered.forEach(r => {
      const screen = r.screen || "system";
      const modal = r.modal || "none";
      tree[screen] = tree[screen] || {};
      tree[screen][modal] = tree[screen][modal] || [];
      tree[screen][modal].push(r);
    });
    return tree;
  }, [filtered]);

  const allCategories = useMemo(() => {
    const set = new Set(["all"]);
    rows.forEach(r => set.add(r.category || "all"));
    return Array.from(set).sort();
  }, [rows]);

  const toggleScreen = (s) => setCollapsedScreens(prev => {
    const n = new Set(prev); n.has(s) ? n.delete(s) : n.add(s); return n;
  });
  const toggleModal = (m) => setCollapsedModals(prev => {
    const n = new Set(prev); n.has(m) ? n.delete(m) : n.add(m); return n;
  });

  const getValue = (row, field) => {
    const draft = drafts[row.key]?.[field];
    if (draft !== undefined) return draft;
    return row[field] ?? "";
  };

  const setDraft = (key, field, value) => {
    setDrafts(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [field]: value },
    }));
  };

  const saveRow = async (row) => {
    const draft = drafts[row.key];
    if (!draft || Object.keys(draft).length === 0) return;
    setSaving(s => ({ ...s, [row.key]: true }));
    try {
      const body = {
        key: row.key,
        value: draft.value !== undefined ? draft.value : row.value,
        surface: draft.surface !== undefined ? draft.surface : row.surface,
        group: draft.group !== undefined ? draft.group : row.group,
        description: draft.description !== undefined ? draft.description : row.description,
        default_value: row.default_value,
        locale: row.locale || "en",
      };
      const r = await fetch(`${API}/admin-copy`, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(await r.text());
      toast.success(`Saved ${row.key}`);
      setRows(prev => prev.map(p => p.key === row.key ? { ...p, ...body } : p));
      setDrafts(prev => { const n = { ...prev }; delete n[row.key]; return n; });
    } catch (err) {
      toast.error("Save failed: " + (err.message || err));
    } finally {
      setSaving(s => ({ ...s, [row.key]: false }));
    }
  };

  const resetRow = (row) => {
    if (!row.default_value) {
      toast.error("No default value set for this row");
      return;
    }
    setDraft(row.key, "value", row.default_value);
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`Delete the copy entry ${row.key}? Frontend will fall back to its hardcoded default.`)) return;
    try {
      const r = await fetch(`${API}/admin-copy/${encodeURIComponent(row.key)}?locale=${row.locale || "en"}`, {
        method: "DELETE",
        headers: headers(),
      });
      if (!r.ok) throw new Error(await r.text());
      toast.success("Deleted");
      setRows(prev => prev.filter(p => p.key !== row.key));
    } catch (err) {
      toast.error("Delete failed: " + (err.message || err));
    }
  };

  const addRow = async () => {
    if (!newRow.key.trim()) { toast.error("key required"); return; }
    setAdding(true);
    try {
      const body = { ...newRow, locale: "en" };
      const r = await fetch(`${API}/admin-copy`, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(await r.text());
      toast.success("Row added");
      setNewRow({ key: "", value: "", surface: "public", group: "", description: "" });
      load();
    } catch (err) {
      toast.error("Add failed: " + (err.message || err));
    } finally {
      setAdding(false);
    }
  };

  const pendingCount = Object.keys(drafts).length;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-copy-editor-page">
      <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sticky top-0 z-20">
        <div className="max-w-[1700px] mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => navigate("/admin/control")} data-testid="copy-back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <Type className="w-5 h-5 text-indigo-600" />
            <div>
              <h1 className="text-lg font-semibold">Copy Editor</h1>
              <p className="text-xs text-gray-500">
                Single source for every UI string. {rows.length} rows. Edits sync platform-wide.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {pendingCount > 0 && (
              <span className="text-xs text-amber-700 bg-amber-100 px-2 py-1 rounded">
                {pendingCount} unsaved
              </span>
            )}
            <button onClick={load} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="copy-refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1700px] mx-auto p-4 sm:p-6">
        {/* Search + filter chips */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search by key, value, group..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="copy-search-input"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {SURFACE_OPTIONS.map((s) => (
              <Button
                key={s.id}
                size="sm"
                variant={surface === s.id ? "default" : "outline"}
                onClick={() => setSurface(s.id)}
                className="text-xs h-8"
                data-testid={`copy-filter-${s.id}`}
              >
                {s.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Iter 9.29 - category filter chips (3rd tier of grouping) */}
        {allCategories.length > 1 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            <span className="text-[11px] uppercase font-semibold text-gray-500 tracking-wider self-center mr-1">Category:</span>
            {allCategories.map(c => (
              <Button
                key={c}
                size="sm"
                variant={categoryFilter === c ? "default" : "outline"}
                onClick={() => setCategoryFilter(c)}
                className="text-xs h-7 capitalize"
                data-testid={`copy-cat-filter-${c}`}
              >
                {c}
              </Button>
            ))}
          </div>
        )}

        {/* Add new row */}
        <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4" data-testid="copy-add-row">
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[220px]">
              <label className="text-[10px] uppercase font-semibold text-gray-500 block mb-1">New key</label>
              <Input
                value={newRow.key}
                onChange={(e) => setNewRow({ ...newRow, key: e.target.value })}
                placeholder="e.g. host.dashboard.welcome_title"
                className="text-xs"
                data-testid="copy-new-key"
              />
            </div>
            <div className="flex-1 min-w-[260px]">
              <label className="text-[10px] uppercase font-semibold text-gray-500 block mb-1">Value</label>
              <Input
                value={newRow.value}
                onChange={(e) => setNewRow({ ...newRow, value: e.target.value })}
                placeholder="Welcome to your dashboard"
                className="text-xs"
                data-testid="copy-new-value"
              />
            </div>
            <div className="w-32">
              <label className="text-[10px] uppercase font-semibold text-gray-500 block mb-1">Surface</label>
              <select
                value={newRow.surface}
                onChange={(e) => setNewRow({ ...newRow, surface: e.target.value })}
                className="w-full h-9 text-xs border border-gray-200 rounded px-2"
                data-testid="copy-new-surface"
              >
                {SURFACE_OPTIONS.filter(s => s.id !== "all").map(s => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </div>
            <Button
              size="sm"
              onClick={addRow}
              disabled={adding || !newRow.key.trim()}
              data-testid="copy-add-btn"
            >
              {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Plus className="w-3 h-3 mr-1" /> Add row</>}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20 text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading copy registry...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-400 text-sm bg-white rounded-xl border border-gray-200">
            No matching copy entries. Try clearing filters or add a new row above.
          </div>
        ) : (
          <div className="space-y-3" data-testid="copy-grid">
            {Object.entries(grouped).map(([screen, modals]) => {
              const screenCollapsed = collapsedScreens.has(screen);
              const screenLabel = SCREEN_LABELS[screen] || screen;
              const screenRowCount = Object.values(modals).reduce((acc, rs) => acc + rs.length, 0);
              return (
                <div key={screen} className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`copy-group-screen-${screen}`}>
                  <button
                    onClick={() => toggleScreen(screen)}
                    className="w-full flex items-center justify-between px-4 py-2.5 bg-indigo-50 hover:bg-indigo-100 border-b border-indigo-100 transition-colors"
                    data-testid={`copy-toggle-screen-${screen}`}
                  >
                    <div className="flex items-center gap-2">
                      {screenCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      <span className="font-semibold text-sm text-indigo-900">{screenLabel}</span>
                      <span className="text-[11px] text-indigo-500 font-mono">{screen}</span>
                    </div>
                    <span className="text-[11px] text-indigo-700 bg-white px-2 py-0.5 rounded-full">
                      {screenRowCount} row{screenRowCount !== 1 ? "s" : ""}
                    </span>
                  </button>
                  {!screenCollapsed && (
                    <div className="divide-y divide-gray-100">
                      {Object.entries(modals).map(([modal, modalRows]) => {
                        const modalKey = `${screen}::${modal}`;
                        const modalCollapsed = collapsedModals.has(modalKey);
                        const modalLabel = MODAL_LABELS[modal] || modal;
                        return (
                          <div key={modalKey} data-testid={`copy-group-modal-${modalKey}`}>
                            {modal !== "none" || Object.keys(modals).length > 1 ? (
                              <button
                                onClick={() => toggleModal(modalKey)}
                                className="w-full flex items-center justify-between px-5 py-2 bg-purple-50/50 hover:bg-purple-100 transition-colors border-b border-purple-100"
                                data-testid={`copy-toggle-modal-${modalKey}`}
                              >
                                <div className="flex items-center gap-2">
                                  {modalCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                  <span className="font-semibold text-xs text-purple-900">{modalLabel}</span>
                                </div>
                                <span className="text-[10px] text-purple-700">{modalRows.length}</span>
                              </button>
                            ) : null}
                            {!modalCollapsed && (
                              <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                  <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                      {COLUMNS.map((c) => (
                                        <th
                                          key={c.key}
                                          className={`px-3 py-1.5 text-left text-[10px] font-semibold uppercase text-gray-600 ${c.width || ""} ${c.muted ? "bg-gray-100/50" : ""}`}
                                        >
                                          {c.label}
                                        </th>
                                      ))}
                                      <th className="px-3 py-1.5 text-right text-[10px] font-semibold uppercase text-gray-600 w-32">
                                        Actions
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {modalRows.map((row) => {
                                      const isDirty = drafts[row.key] && Object.keys(drafts[row.key]).length > 0;
                                      return (
                                        <tr
                                          key={row.key}
                                          className={isDirty ? "bg-amber-50/40" : "hover:bg-gray-50"}
                                          data-testid={`copy-row-${row.key}`}
                                        >
                                          {COLUMNS.map((c) => {
                                            const val = getValue(row, c.key);
                                            if (c.readonly) {
                                              return (
                                                <td key={c.key} className={`px-2 py-1 ${c.width} font-mono text-[11px] text-gray-700`}>
                                                  <code className="break-all">{val}</code>
                                                </td>
                                              );
                                            }
                                            if (c.muted) {
                                              return (
                                                <td key={c.key} className={`px-2 py-1 ${c.width} text-[11px] text-gray-400`}>
                                                  <span className="line-clamp-2">{val || "-"}</span>
                                                </td>
                                              );
                                            }
                                            const isLong = c.key === "value" && (val || "").length > 80;
                                            return (
                                              <td key={c.key} className={`px-2 py-1 ${c.width}`}>
                                                {isLong ? (
                                                  <Textarea
                                                    value={val || ""}
                                                    onChange={(e) => setDraft(row.key, c.key, e.target.value)}
                                                    rows={2}
                                                    className="text-xs"
                                                    data-testid={`copy-cell-${row.key}-${c.key}`}
                                                  />
                                                ) : (
                                                  <input
                                                    type="text"
                                                    value={val}
                                                    onChange={(e) => setDraft(row.key, c.key, e.target.value)}
                                                    className="w-full text-xs px-1 py-1 border border-gray-200 rounded"
                                                    data-testid={`copy-cell-${row.key}-${c.key}`}
                                                  />
                                                )}
                                              </td>
                                            );
                                          })}
                                          <td className="px-3 py-2 w-32">
                                            <div className="flex items-center justify-end gap-1">
                                              {isDirty && (
                                                <Button
                                                  size="sm"
                                                  onClick={() => saveRow(row)}
                                                  disabled={saving[row.key]}
                                                  className="h-7 px-2 text-xs"
                                                  data-testid={`copy-save-${row.key}`}
                                                >
                                                  {saving[row.key] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                                </Button>
                                              )}
                                              {row.default_value && row.default_value !== row.value && (
                                                <button
                                                  onClick={() => resetRow(row)}
                                                  className="p-1.5 hover:bg-gray-100 rounded"
                                                  title="Reset to default"
                                                  data-testid={`copy-reset-${row.key}`}
                                                >
                                                  <RotateCcw className="w-3.5 h-3.5 text-gray-500" />
                                                </button>
                                              )}
                                              <button
                                                onClick={() => deleteRow(row)}
                                                className="p-1.5 hover:bg-gray-100 rounded"
                                                title="Delete"
                                                data-testid={`copy-delete-${row.key}`}
                                              >
                                                <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                              </button>
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <p className="mt-4 text-xs text-gray-500">
          Edits sync immediately to all consumer + host surfaces via the <code>useAdminCopy</code> hook.
          Frontend keys missing in this table fall back to their hardcoded default.
        </p>
      </main>
    </div>
  );
}
